import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'

interface Match {
  idea_id: string
  code: string
  title: string
  snippet: string
  cosine: number
  glm: number | null
  reason: string
}

interface BatchItem {
  row: number
  num: string
  bu_name: string
  bu_matched?: boolean
  title: string
  problem: string
  solution: string
  score?: number
  color?: 'red' | 'yellow' | 'green'
  matches_all?: Match[]
  matches_bu?: Match[]
}

interface BatchSummary {
  id: string
  filename: string
  status: 'pending' | 'running' | 'done' | 'failed'
  total: number
  processed: number
  error: string | null
  created_at: string
}

interface BatchDetail extends BatchSummary {
  items: BatchItem[]
}

// Пороги согласованы с заказчиком: красный ≥75, жёлтый 50–74, зелёный <50.
const ROW_TINT: Record<string, string> = {
  red: 'bg-red-50 dark:bg-red-900/20',
  yellow: 'bg-amber-50 dark:bg-amber-900/20',
  green: 'bg-green-50 dark:bg-green-900/20',
}

const BADGE: Record<string, string> = {
  red: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700',
  yellow: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700',
  green: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-200 dark:border-green-700',
}

const VERDICT_LABEL: Record<string, string> = {
  red: 'Есть похожие',
  yellow: 'Близкие',
  green: 'Похожих нет',
}

export const Route = createFileRoute('/batch-similarity')({
  beforeLoad: async () => {
    const s = useAuth.getState()
    if (s.loading) await s.load()
    const user = useAuth.getState().user
    if (!user) throw redirect({ to: '/login' })
    if (user.role !== 'admin' && user.role !== 'manager') throw redirect({ to: '/profile' })
  },
  component: BatchSimilarityPage,
})

function BatchSimilarityPage() {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)

  const batches = useQuery({
    queryKey: ['simbatches'],
    queryFn: () => api<BatchSummary[]>('/api/similarity-batches'),
  })

  const detail = useQuery({
    queryKey: ['simbatch', activeId],
    queryFn: () => api<BatchDetail>(`/api/similarity-batches/${activeId}`),
    enabled: activeId !== null,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'pending' || s === 'running' ? 2500 : false
    },
  })

  async function doUpload() {
    if (!file) return
    setUploading(true)
    setUploadError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch('/api/similarity-batches', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      })
      if (!r.ok) {
        const body = (await r.json().catch(() => null)) as { detail?: string } | null
        throw new Error(body?.detail ?? `Ошибка загрузки (${r.status})`)
      }
      const created = (await r.json()) as BatchSummary
      setActiveId(created.id)
      setExpanded(null)
      setFile(null)
      void queryClient.invalidateQueries({ queryKey: ['simbatches'] })
    } catch (e: unknown) {
      setUploadError(e instanceof Error ? e.message : 'Не удалось загрузить файл')
    } finally {
      setUploading(false)
    }
  }

  const d = detail.data

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Проверка Excel на похожие идеи
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Загрузите шаблон выгрузки (лист «Отчет»). Каждая идея будет проверена на похожие
          дважды: по всей базе и по её БЕ. Красный — есть похожие (≥75), жёлтый — близкие
          (50–74), зелёный — похожих нет (&lt;50).
        </p>
      </div>

      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block text-sm"
          />
          <button
            onClick={() => void doUpload()}
            disabled={!file || uploading}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
          >
            {uploading ? 'Загружаю…' : 'Проверить'}
          </button>
          {(batches.data?.length ?? 0) > 0 && (
            <select
              value={activeId ?? ''}
              onChange={(e) => {
                setActiveId(e.target.value || null)
                setExpanded(null)
              }}
              className="text-sm border border-zinc-300 dark:border-zinc-600 rounded-md px-2 py-2 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
            >
              <option value="">Прошлые проверки…</option>
              {batches.data?.map((b) => (
                <option key={b.id} value={b.id}>
                  {new Date(b.created_at).toLocaleString('ru-RU')} — {b.filename} ({b.status})
                </option>
              ))}
            </select>
          )}
        </div>
        {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
      </div>

      {d && (d.status === 'pending' || d.status === 'running') && (
        <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-4">
          <p className="text-sm text-zinc-700 dark:text-zinc-300 mb-2">
            Проверяю идеи… {d.processed} из {d.total}
          </p>
          <div className="h-2 bg-zinc-200 dark:bg-zinc-700 rounded overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{ width: `${d.total ? Math.round((d.processed / d.total) * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {d && d.status === 'failed' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-800 dark:text-red-200">
          Проверка не удалась: {d.error ?? 'неизвестная ошибка'}
        </div>
      )}

      {d && d.status === 'done' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              {d.filename}: {d.total} идей ·{' '}
              {(['red', 'yellow', 'green'] as const)
                .map((c) => `${VERDICT_LABEL[c].toLowerCase()}: ${d.items.filter((i) => i.color === c).length}`)
                .join(' · ')}
            </p>
            <a
              href={`/api/similarity-batches/${d.id}/export.xlsx`}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 dark:bg-zinc-200 dark:hover:bg-zinc-300 text-white dark:text-zinc-900 rounded-md text-sm"
            >
              📥 Выгрузить в Excel
            </a>
          </div>

          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-700/50 text-left text-zinc-500 dark:text-zinc-400">
                <tr>
                  <th className="px-4 py-2 font-medium">Номер</th>
                  <th className="px-4 py-2 font-medium">Наименование идеи</th>
                  <th className="px-4 py-2 font-medium">БЕ</th>
                  <th className="px-4 py-2 font-medium">Совпадение</th>
                  <th className="px-4 py-2 font-medium">Вердикт</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700">
                {d.items.map((it, idx) => (
                  <BatchRow
                    key={it.row}
                    item={it}
                    expanded={expanded === idx}
                    onToggle={() => setExpanded(expanded === idx ? null : idx)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function BatchRow({
  item,
  expanded,
  onToggle,
}: {
  item: BatchItem
  expanded: boolean
  onToggle: () => void
}) {
  const color = item.color ?? 'green'
  return (
    <>
      <tr
        onClick={onToggle}
        className={`cursor-pointer hover:brightness-95 dark:hover:brightness-110 ${ROW_TINT[color]}`}
      >
        <td className="px-4 py-2 text-zinc-500 whitespace-nowrap">{item.num}</td>
        <td className="px-4 py-2 text-zinc-900 dark:text-zinc-100">{item.title}</td>
        <td className="px-4 py-2 text-zinc-500">{item.bu_name}</td>
        <td className="px-4 py-2 whitespace-nowrap">
          <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${BADGE[color]}`}>
            {item.score ?? 0}%
          </span>
        </td>
        <td className="px-4 py-2 text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
          {VERDICT_LABEL[color]} {expanded ? '▾' : '▸'}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-zinc-50 dark:bg-zinc-900/40">
            <div className="grid md:grid-cols-2 gap-4">
              <MatchList title="По всей базе" matches={item.matches_all ?? []} />
              {item.bu_matched ? (
                <MatchList title={`По БЕ: ${item.bu_name}`} matches={item.matches_bu ?? []} />
              ) : (
                <div className="text-sm text-zinc-500">
                  БЕ «{item.bu_name}» не найдена в справочнике — поиск по БЕ не выполнялся.
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function MatchList({ title, matches }: { title: string; matches: Match[] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase text-zinc-500 mb-2">{title}</h4>
      {matches.length === 0 ? (
        <p className="text-sm text-zinc-500">Похожих не найдено.</p>
      ) : (
        <ul className="space-y-2">
          {matches.map((m) => (
            <li
              key={m.idea_id}
              className="border border-zinc-200 dark:border-zinc-700 rounded-lg p-2 bg-white dark:bg-zinc-800"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-zinc-500">{m.code}</span>
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{m.title}</span>
                <span className={`ml-auto inline-block px-1.5 py-0.5 rounded border text-xs ${BADGE[m.glm !== null ? (m.glm >= 75 ? 'red' : m.glm >= 50 ? 'yellow' : 'green') : 'green']}`}>
                  {m.glm !== null ? `${m.glm}%` : '<50%'}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{m.snippet}</p>
              {m.reason && <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1 italic">{m.reason}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
